import { useEffect, useState } from 'react';
import { ContactRound, History, Pencil, Phone, Save, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth, hasRole } from '../lib/auth';
import { fmtDateTime } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { Module } from './ui/Module';

/**
 * CONTACTO DE LA INSPECCIÓN — arriba de todo en la pestaña Scheduling (Manuel 2026-09-23, caso
 * Glenn Schreck): la compañía de inspección, el inspector y los dos teléfonos, para saber a quién
 * llamar si hay que reagendar. Cada guardado queda con quién y cuándo (versiones, no se pisa).
 */
type Fields = { inspection_company: string | null; inspector_name: string | null; inspector_phone: string | null; company_phone: string | null };
type Row = Fields & { id: string; set_by: string | null; set_at: string };
type Resp = { current: Row | null; history: Row[] };

const VACIO: Fields = { inspection_company: '', inspector_name: '', inspector_phone: '', company_phone: '' };

function Tel({ value }: { value: string | null }) {
  if (!value) return <span className="muted">—</span>;
  const digits = value.replace(/[^0-9+]/g, '');
  return (
    <a href={`tel:${digits || value}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
      <Phone size={12} strokeWidth={1.75} /> {value}
    </a>
  );
}

export function InspectionContactBox({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const canEdit = hasRole(me, 'office');
  const [data, setData] = useState<Resp | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Fields>(VACIO);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get<Resp>(`/api/claims/${claimId}/inspection-contact`).then((r) => { if (vivo) setData(r); }).catch(() => {});
    return () => { vivo = false; };
  }, [claimId]);

  function startEdit() {
    const c = data?.current;
    setForm({
      inspection_company: c?.inspection_company ?? '', inspector_name: c?.inspector_name ?? '',
      inspector_phone: c?.inspector_phone ?? '', company_phone: c?.company_phone ?? '',
    });
    setErr(''); setEditing(true);
  }
  async function save() {
    if (!Object.values(form).some((v) => (v ?? '').trim())) { setErr(t('ic.needOne')); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.put<Resp>(`/api/claims/${claimId}/inspection-contact`, form);
      setData(r); setEditing(false);
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBusy(false); }
  }

  const c = data?.current ?? null;
  const preview = c
    ? [c.inspection_company, c.inspector_name, c.inspector_phone ?? c.company_phone].filter(Boolean).join(' · ')
    : t('ic.empty');
  const campo = (k: keyof Fields, label: string) => (
    <label style={{ display: 'grid', gap: 3, fontSize: 'var(--fs-2xs)' }}>
      <span className="muted">{label}</span>
      <input className="input sm" value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
    </label>
  );

  return (
    <Module id={`inspection-contact:${claimId}`} icon={ContactRound} title={t('ic.title')} preview={preview}>
      <div style={{ display: 'grid', gap: 10 }}>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ic.hint')}</div>
        {editing ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
              {campo('inspection_company', t('ic.company'))}
              {campo('company_phone', t('ic.companyPhone'))}
              {campo('inspector_name', t('ic.inspector'))}
              {campo('inspector_phone', t('ic.inspectorPhone'))}
            </div>
            {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>{err}</div>}
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn sm" disabled={busy} onClick={save}><Save size={13} strokeWidth={1.75} /> {t('ic.save')}</button>
              <button className="btn ghost sm" disabled={busy} onClick={() => setEditing(false)}><X size={13} strokeWidth={1.75} /> {t('ic.cancel')}</button>
            </div>
          </div>
        ) : c ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, fontSize: 'var(--fs-xs)' }}>
              <div><div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ic.company')}</div><div style={{ fontWeight: 600 }}>{c.inspection_company || '—'}</div></div>
              <div><div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ic.companyPhone')}</div><Tel value={c.company_phone} /></div>
              <div><div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ic.inspector')}</div><div style={{ fontWeight: 600 }}>{c.inspector_name || '—'}</div></div>
              <div><div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ic.inspectorPhone')}</div><Tel value={c.inspector_phone} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-2xs)' }}>
              {/* LA HUELLA: quién lo puso y cuándo (regla del dueño). */}
              <span className="muted">{t('ic.setBy')} <b>{c.set_by ?? '—'}</b> · {fmtDateTime(c.set_at)}</span>
              <span style={{ flex: 1 }} />
              {data && data.history.length > 0 && (
                <button className="btn ghost sm" onClick={() => setShowHistory(!showHistory)}>
                  <History size={12} strokeWidth={1.75} /> {t('ic.history')} ({data.history.length})
                </button>
              )}
              {canEdit && <button className="btn ghost sm" onClick={startEdit}><Pencil size={12} strokeWidth={1.75} /> {t('ic.edit')}</button>}
            </div>
            {showHistory && data && (
              <div style={{ display: 'grid', gap: 4, borderTop: '1px dashed var(--line)', paddingTop: 6, fontSize: 'var(--fs-2xs)' }}>
                {data.history.map((h) => (
                  <div key={h.id} className="muted">
                    {fmtDateTime(h.set_at)} · <b>{h.set_by ?? '—'}</b>: {[h.inspection_company, h.inspector_name, h.inspector_phone, h.company_phone].filter(Boolean).join(' · ') || '—'}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-xs)' }}>
            <span className="muted">{t('ic.empty')}</span>
            {canEdit && <button className="btn sm" onClick={startEdit}><Pencil size={12} strokeWidth={1.75} /> {t('ic.add')}</button>}
          </div>
        )}
      </div>
    </Module>
  );
}
